import { Component, computed, effect, OnDestroy, OnInit, signal, Signal, WritableSignal, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { UpdatePageTitle } from '../../../../store/app/app.actions';
import { AsyncPipe, NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { AccountingApiService } from '../../../../_services/accounting-service/accounting-api/accounting-api.service';
import {
  AccountingAccount,
  AccountingConnectionStatus,
  AccountingProvider,
  AccountingProviderCompany,
  TenantAccountOption,
} from '../../../../_interfaces/accounting.interface';
import { ToastService } from '../../../../_services/toast-service/toast.service';
import { filter, map, Observable, Subscription, take } from 'rxjs';
import { ActivatedRoute, RouterLink, RouterOutlet } from '@angular/router';
import { AccountingService } from '../_services/accounting/accounting.service';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { SetAccountingProviderStateFromAccountingConnection } from '../../../../store/accounting/accounting.actions';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { UpgradeToPremiumOverlayComponent } from '../../../../shared/components/upgrade-to-premium-overlay/upgrade-to-premium-overlay.component';
import { SubscriptionSelectors } from '../../../../store/subscription/subscription.selectors';
import { AccountingModalService } from '../_services/modals/accounting-modal.service';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../../shared/components/status-badge/status-badge.component';

@Component({
  selector: 'app-integrations',
  imports: [
    NgOptimizedImage,
    AsyncPipe,
    RouterLink,
    LoaderComponent,
    RouterOutlet,
    TooltipModule,
    NgTemplateOutlet,
    UpgradeToPremiumOverlayComponent,
    NgClass,
    StatusBadgeComponent,
    NgbTooltip,
  ],
  templateUrl: './integrations.component.html',
})
export class IntegrationsComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly route = inject(ActivatedRoute);
  readonly accountingApiService = inject(AccountingApiService);
  readonly accountingService = inject(AccountingService);
  readonly accountingModalService = inject(AccountingModalService);
  readonly toastService = inject(ToastService);
  readonly modalService = inject(NgbModal);

  readonly hasAccounting$ = this.store.select(SubscriptionSelectors.hasAccounting);

  // Loading state to prevent flash of the "no accounting" screen
  readonly subscriptionLoading = signal(true);
  readonly hasAccounting = signal<boolean | undefined>(undefined);

  readonly reconnectParam: Signal<string> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return queryParams['reconnect'] as string;
  });

  readonly updateParam: Signal<string> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return queryParams['update'] as string;
  });

  protected xeroBusy = computed(() => this.accountingService.busyConnectingXERO());
  protected qbBusy = computed(() => this.accountingService.busyConnectingQB());
  protected readonly AccountingProviderCompany = AccountingProviderCompany;
  protected readonly AccountingConnectionStatus = AccountingConnectionStatus;
  protected providerList$: Observable<AccountingProvider[]> | undefined;
  readonly subscriptions = new Subscription();

  protected activeProvider: Signal<AccountingProviderCompany | undefined> = computed(() => {
    return this.accountingService.provider();
  });

  protected newConnection: Signal<boolean | undefined> = computed(() => {
    return this.accountingService.providerNewBankAccountsCreated();
  });

  protected selectedTenant: Signal<TenantAccountOption | undefined> = computed(() => {
    return this.accountingService.tenantDetail();
  });

  protected sourceAccount: Signal<AccountingAccount | undefined> = computed(() => {
    return this.accountingService.sourceAccount();
  });

  protected connectionStatus: Signal<AccountingConnectionStatus | undefined> = computed(() => {
    if (this.updateParam() === 'source-account') {
      this.toastService.show(`Successfully updated your source account`, 'iconoir-check-circle');
    }
    if (this.reconnectParam() === 'complete') {
      if (this.connectionError()) {
        this.toastService.show(
          `Something went wrong with your ${this.activeProvider()} connection. Please try again.`,
          'iconoir-xmark',
        );
      } else if (this.connectionComplete()) {
        this.toastService.show(
          `Successfully ${this.newConnection() ? 'connected' : 'reconnected'} to your ${this.activeProvider()} account`,
          'iconoir-check-circle',
        );
      }
    }
    return this.accountingService.connectionStatus();
  });

  protected connectionError: Signal<boolean> = computed(() => {
    return (
      this.accountingService.connectionStatus() === AccountingConnectionStatus.Error ||
      this.accountingService.connectionStatus() === AccountingConnectionStatus.HttpError ||
      this.accountingService.connectionStatus() === AccountingConnectionStatus.AuthError
    );
  });

  protected connectionComplete: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.Complete;
  });

  protected connectionPending: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.Pending;
  });

  protected organisationNotSelected: Signal<boolean> = computed(
    () => this.connectionStatus() === AccountingConnectionStatus.HasTenants,
  );

  protected accountingDisconnected: Signal<boolean> = computed(
    () => this.connectionStatus() === AccountingConnectionStatus.Disconnected,
  );

  contraAccounts: WritableSignal<AccountingAccount[]> = signal([]);

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Integrations'));
    this.store.dispatch(new SetAccountingProviderStateFromAccountingConnection());

    // Wait for the subscription state to load, then update the hasAccounting signal
    // This prevents the "no accounting" overlay from flashing on page load
    this.subscriptions.add(
      this.hasAccounting$
        .pipe(
          filter((hasAccounting) => hasAccounting !== undefined), // Wait for actual value
          take(1), // Only take the first real value
        )
        .subscribe((hasAccounting) => {
          this.hasAccounting.set(hasAccounting);
          this.subscriptionLoading.set(false);
        }),
    );

    // Load contra-accounts when the connection is complete
    effect(() => {
      if (this.connectionComplete()) {
        this.subscriptions.add(
          this.accountingApiService.getAccountingContraAccount().subscribe({
            next: (data) => {
              this.contraAccounts.set(data);
            },
          }),
        );
      }
    });
  }
  ngOnInit(): void {
    this.providerList$ = this.accountingApiService.getAccountingProviders().pipe(
      map((providers) =>
        providers.map((provider) => ({
          ...provider,
          logo: this.getLogo(provider.name),
        })),
      ),
    );
  }

  getLogo(providerName: AccountingProviderCompany) {
    if (providerName === AccountingProviderCompany.XERO) return { img: 'xero-logo.svg', width: 70, height: 70 };
    if (providerName === AccountingProviderCompany.QUICKBOOKS)
      return { img: 'quickbook-logo.svg', width: 270, height: 54 };
    return '';
  }

  onConnect(provider: AccountingProviderCompany) {
    this.accountingService.onConnect(provider);
  }

  deleteConnection() {
    this.accountingModalService.deleteConnection();
  }

  reconnectConnection(provider: AccountingProviderCompany) {
    this.accountingService.reconnectConnection(provider);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly statusBadgeDepartments = statusBadgeDepartments;
}
